import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from "react";
import { injectBrandingCSS, injectThemeCSS } from "../config/branding";
import {
  THEME_PRESETS,
  getPresetById,
  mergeCustomTheme,
  type ThemePreset,
} from "../config/theme-presets";

type Theme = "light" | "dark" | "orange";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  activePresetId: string;
  customTheme: Partial<ThemePreset> | null;
  activeTheme: ThemePreset;
  isLoading: boolean;
  setPreset: (id: string) => void;
  setCustomTheme: (overrides: Partial<ThemePreset>) => void;
  resetCustomTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = "dashboard-theme";
const PRESET_STORAGE_KEY = "dashboard-preset";
const CUSTOM_THEME_STORAGE_KEY = "dashboard-custom-theme";

function loadThemeFromStorage(fallback: Theme): Theme {
  if (typeof window === "undefined") return fallback;

  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (!stored) return fallback;

  if (stored === "light" || stored === "dark" || stored === "orange") {
    return stored;
  }

  try {
    const parsed = JSON.parse(stored);
    if (parsed === "light" || parsed === "dark" || parsed === "orange") {
      return parsed;
    }
  } catch {
    // Ignore legacy malformed values and fall back.
  }

  return fallback;
}

function saveThemeToStorage(theme: Theme): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // localStorage full or unavailable
  }
}

function loadFromStorage<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const stored = localStorage.getItem(key);
    if (stored === null) return fallback;
    return JSON.parse(stored) as T;
  } catch {
    return fallback;
  }
}

function saveToStorage(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage full or unavailable
  }
}

async function savePrefsToAPI(data: {
  presetId?: string;
  customTheme?: Partial<ThemePreset> | null;
  darkMode?: string;
}): Promise<void> {
  try {
    await fetch("/api/user/preferences", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  } catch {
    // API unavailable — localStorage fallback is already saved
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => loadThemeFromStorage("orange"));

  const [activePresetId, setActivePresetId] = useState<string>(() =>
    loadFromStorage(PRESET_STORAGE_KEY, "salesgrid")
  );

  const [customTheme, setCustomThemeState] = useState<Partial<ThemePreset> | null>(() =>
    loadFromStorage<Partial<ThemePreset> | null>(CUSTOM_THEME_STORAGE_KEY, null)
  );

  const [isLoading, setIsLoading] = useState(true);

  // Resolve the active theme by merging preset + custom overrides
  const activeTheme = useMemo((): ThemePreset => {
    const base = getPresetById(activePresetId) ?? THEME_PRESETS[0]!;
    if (customTheme && Object.keys(customTheme).length > 0) {
      return mergeCustomTheme(base, customTheme);
    }
    return base;
  }, [activePresetId, customTheme]);

  // Load preferences from API on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/user/preferences");
        if (!res.ok) throw new Error("API error");
        const json = await res.json() as {
          success: boolean;
          data: { presetId?: string; customTheme?: Partial<ThemePreset> | null; darkMode?: string };
        };
        if (cancelled || !json.success) return;
        const { data } = json;
        if (data.presetId && getPresetById(data.presetId)) {
          setActivePresetId(data.presetId);
          saveToStorage(PRESET_STORAGE_KEY, data.presetId);
        }
        if (data.customTheme !== undefined) {
          setCustomThemeState(data.customTheme ?? null);
          saveToStorage(CUSTOM_THEME_STORAGE_KEY, data.customTheme);
        }
        if (data.darkMode === "light" || data.darkMode === "dark" || data.darkMode === "orange") {
          setThemeState(data.darkMode as Theme);
          saveThemeToStorage(data.darkMode as Theme);
        }
      } catch {
        // Fall back to localStorage values (already loaded in initial state)
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Apply theme class + inject CSS vars whenever theme/preset changes
  useEffect(() => {
    const root = document.documentElement;
    // Remove all theme classes first
    root.classList.remove("dark", "orange");

    if (theme === "dark") {
      root.classList.add("dark");
    } else if (theme === "orange") {
      root.classList.add("orange");
    }
    saveThemeToStorage(theme);

    // Use new theme injection if we have an active theme
    // Orange theme uses dark mode base colors
    injectThemeCSS(activeTheme, theme === "dark" || theme === "orange");
  }, [theme, activeTheme]);

  // Inject branding CSS on mount as fallback
  useEffect(() => {
    injectBrandingCSS();
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      // Cycle: light -> dark -> orange -> light
      const next: Theme = prev === "light" ? "dark" : prev === "dark" ? "orange" : "light";
      savePrefsToAPI({ darkMode: next });
      return next;
    });
  }, []);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    savePrefsToAPI({ darkMode: newTheme });
  }, []);

  const setPreset = useCallback((id: string) => {
    setActivePresetId(id);
    setCustomThemeState(null);
    saveToStorage(PRESET_STORAGE_KEY, id);
    saveToStorage(CUSTOM_THEME_STORAGE_KEY, null);
    savePrefsToAPI({ presetId: id, customTheme: null });
  }, []);

  const setCustomTheme = useCallback((overrides: Partial<ThemePreset>) => {
    setCustomThemeState((prev) => {
      const merged = { ...(prev ?? {}), ...overrides };
      saveToStorage(CUSTOM_THEME_STORAGE_KEY, merged);
      savePrefsToAPI({ customTheme: merged });
      return merged;
    });
  }, []);

  const resetCustomTheme = useCallback(() => {
    setCustomThemeState(null);
    saveToStorage(CUSTOM_THEME_STORAGE_KEY, null);
    savePrefsToAPI({ customTheme: null });
  }, []);

  const value = useMemo<ThemeContextType>(
    () => ({
      theme,
      toggleTheme,
      setTheme,
      activePresetId,
      customTheme,
      activeTheme,
      isLoading,
      setPreset,
      setCustomTheme,
      resetCustomTheme,
    }),
    [theme, toggleTheme, setTheme, activePresetId, customTheme, activeTheme, isLoading, setPreset, setCustomTheme, resetCustomTheme]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

/** Returns the fully resolved active ThemePreset */
export function useActiveTheme(): ThemePreset {
  const { activeTheme } = useTheme();
  return activeTheme;
}
